// The per-operator project star — "keep this one where I can find it".
//
// Deliberately NOT one of the project controls in `operator-controls.tsx`: those
// do something a CLIENT experiences and are gated on Full access, while this is
// a private bookmark keyed to the caller's own email (migration 0158). It
// changes nothing for anyone else, which is why a read-only reviewer gets it
// too — they are the person with the most projects to keep track of.
//
// Optimistic on purpose. A star is a one-bit toggle an operator flicks down a
// list of a hundred rows, and a spinner per row would make the list feel broken;
// on failure it snaps back and says so, which is the only honest way to be
// optimistic (doctrine 1.5).
import { useToast } from "@recruiter/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError, setProjectStar } from "../lib/api";
import { adminKeys } from "../lib/queries";
import "./star-toggle.css";

export function StarToggle({
  projectId,
  starred,
  title,
}: {
  projectId: string;
  starred: boolean;
  /** The project's display title, for the button's accessible name. A row of
   *  identical "Star" buttons is unusable with a screen reader. */
  title: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const toggle = useMutation({
    mutationFn: (next: boolean) => setProjectStar(projectId, next),
    // Both lists that render a star are keyed under the explorer, and a project
    // appears in both (the cross-user table and its owner's page), so refresh
    // the pair rather than the one the click came from.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.explorerProjects });
      void queryClient.invalidateQueries({ queryKey: ["admin", "explorer", "user"] });
    },
    onError: (error: unknown) => {
      toast.add({
        title: starred ? "Couldn't unstar that project" : "Couldn't star that project",
        description: error instanceof ApiError ? error.detail : undefined,
        data: { tone: "danger" },
      });
    },
  });

  // While the write is in flight, show what the operator ASKED for. The query
  // invalidation above then confirms it from the server.
  const shown = toggle.isPending ? Boolean(toggle.variables) : starred;

  return (
    <button
      type="button"
      className={shown ? "st-star st-star--on" : "st-star"}
      aria-pressed={shown}
      aria-label={shown ? `Unstar ${title}` : `Star ${title}`}
      title={shown ? "Starred. Click to remove." : "Star this project to filter to it later."}
      onClick={() => toggle.mutate(!shown)}
    >
      {/* Filled vs outlined off the SAME path, so the two states are the same
          star and not two icons that happen to look alike. */}
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
        <path
          d="M12 3.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L12 16.77l-5.2 2.73.99-5.78-4.21-4.1 5.82-.85z"
          fill={shown ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
